import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { OrderStatusPageConfig } from '@front-platform/content-builder';
import { orderStatusPageResolveConfig } from '@front-platform/content-builder';
import { concat, interval } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Component, OnDestroy, OnInit, signal, input } from '@angular/core';
import { switchMap, take, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { DoodOrderModel } from '@store/order/order.model';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { CartService } from '@core/services/cart/cart.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { EventTrackerService } from '@core/services/event-tracker/event-tracker.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { KioskService } from '@core/services/kiosk/kiosk.service';
import { Store } from '@ngrx/store';
import { PAGE } from '@store/page/page.action';
import { BlockListComponent } from '@core/components/block-list/block-list.component';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
export interface OrderStatusPageRuntimeConfig {
  'body'?: unknown[];
}

export type OrderStatusPageConfigInput = Partial<
  Omit<OrderStatusPageConfig, keyof OrderStatusPageRuntimeConfig> & OrderStatusPageRuntimeConfig
>;
export type OrderStatusPageResolvedConfig = Omit<
  OrderStatusPageConfig,
  keyof OrderStatusPageRuntimeConfig
> &
  Partial<OrderStatusPageRuntimeConfig>;
export interface OrderStatusPageContract extends ConfigComponentContract<
  OrderStatusPageConfigInput,
  OrderStatusPageResolvedConfig
> {}

@Component({
  selector: 'app-order-status-page',
  templateUrl: './order-status-page.component.html',
  imports: [BlockListComponent, ContentBuilderColorPipe],
})
export class OrderStatusPageComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, OrderStatusPageContract
{
  readonly config = input<OrderStatusPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    OrderStatusPageConfig,
    OrderStatusPageRuntimeConfig
  >(orderStatusPageResolveConfig, () => ({ config: this.config() }));
  orderAlreadyTracked = false;

  /** When true, shows overlay to paste mock order JSON instead of calling API */
  isMockMode = signal(false);

  mockOrderJson = signal('');

  mockOrderParseError = signal<string | null>(null);

  /** When false, mock dialog is minimized to a small expand button */
  isMockDialogExpanded = signal(true);

  get body(): unknown[] {
    return this.resolvedConfig()['body'] as unknown as unknown[];
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get backgroundImageCss(): string | null {
    const url = this.backgroundImage?.url;
    return url ? `url(${url})` : null;
  }

  private orderId = '';

  constructor(
    private orderSelector: OrderStoreSelector,
    private readonly cartService: CartService,
    private readonly ordersService: OrdersService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly notificationService: NotificationService,
    private readonly eventTrackerService: EventTrackerService,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly kioskService: KioskService,
    private readonly store: Store,
  ) {
    super();
    this.orderId = this.activatedRoute.snapshot.params['orderId'];
    this.isMockMode.set(this.orderId === 'test');

    const urlSearchParams = new URLSearchParams(window.location.search);
    let shareCode: string | undefined;
    if (urlSearchParams.has('share_code')) {
      shareCode = urlSearchParams.get('share_code') ?? undefined;
    }

    this.orderSelector
      .selectOrder(this.orderId)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(order => this.orderConfirmedEvent(order)),
      )
      .subscribe();

    if (!this.isMockMode()) {
      // Fallback if websocket is not available — progressive backoff: 2s, 4s, 8s, 16s (capped)
      const progressivePolling$ = concat(
        interval(4000).pipe(take(1)),
        interval(8000).pipe(take(1)),
        interval(16000).pipe(take(1)),
        interval(32000),
      );

      progressivePolling$
        .pipe(
          takeUntil(this._destroyerRef),
          switchMap(() => this.ordersService.loadOrderById$(this.orderId, shareCode)),
          switchMap(() => this.orderSelector.selectOrder(this.orderId)),
          takeWhile(order => !order || !['CANCELLED', 'COLLECTED'].includes(order.status)),
        )
        .subscribe();

      this.loadOrder(this.orderId, shareCode);
    } else {
      this.ordersService.deActiveOrderToStore();
    }

    this.store.dispatch(PAGE.orderConfirmationPageLaunched());
  }

  orderConfirmedEvent(order: DoodOrderModel | undefined): void {
    if (
      !this.orderAlreadyTracked &&
      order?.status &&
      ['WAITING', 'PREPARATION', 'READY', 'COLLECTED'].includes(order.status)
    ) {
      this.kioskService.printOrder$(order).subscribe();
      this.eventTrackerService.dispatch(EventTrackerService.EventOrderPaid, order);
      this.orderAlreadyTracked = true;
    }
  }

  ngOnInit(): void {
    // Do not display order notification on this page
    this.notificationService.setHandleNotifications(false);
    this.navigationHistoryService.pushState('home', '/');
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'order-status',
      this.activatedRoute.snapshot,
    );

    this.cartService.clearCart();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    // Restore notification display when leaving this page
    this.notificationService.setHandleNotifications(true);
    this.ordersService.clearOrders();
  }

  loadOrder(orderId: string, shareCode?: string): void {
    this.ordersService.deActiveOrderToStore();
    this.ordersService.setLoadingToStore();
    this.ordersService
      .loadOrderById$(orderId, shareCode)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(order => this.ordersService.setActiveOrderToStore(order)),
      )
      .subscribe();
  }

  onMockOrderInputChange(): void {
    this.mockOrderParseError.set(null);
    const trimmed = this.mockOrderJson().trim();
    if (!trimmed) {
      return;
    }
    try {
      const parsed = JSON.parse(trimmed) as DoodOrderModel;
      if (!parsed || typeof parsed.id !== 'string') {
        this.mockOrderParseError.set('Invalid format: order must have an id');
        return;
      }
      this.ordersService.injectMockOrder(parsed, this.orderId);
    } catch (e) {
      this.mockOrderParseError.set(e instanceof Error ? e.message : 'Invalid JSON');
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(OrderStatusPageComponent, { 'keyAliases': {} });
